/**
 * ENG-8357 — the ONE definition of how a remote-MCP stdio proxy entry
 * authenticates.
 *
 * Two independent pieces of code have to agree on this, and they used to
 * disagree:
 *
 *   1. `packages/mcp/src/remote-oauth-proxy.ts` — the proxy the agent's tool
 *      calls actually flow through. It reads its config from `process.env`
 *      (populated from the `.mcp.json` entry's `env` block by Claude Code).
 *   2. `apps/cli/src/lib/connectivity-probe-context.ts` — the connectivity
 *      probe, which does NOT spawn the proxy for an `mcp_tools_list` target.
 *      It RECONSTRUCTS the equivalent direct HTTP call from the same `env`
 *      block and issues it itself.
 *
 * The probe hardcoded `Authorization: Bearer <token>` and read only
 * `AGT_REMOTE_MCP_URL` + `AGT_REMOTE_MCP_TOKEN_VAR`, while the proxy had since
 * grown `AGT_REMOTE_MCP_AUTH_HEADER` + `AGT_REMOTE_MCP_EXTRA_HEADERS`
 * (ENG-7748, for Anchor's `anchor-api-key` scheme). For any provider on a
 * non-Bearer scheme the agent would authenticate correctly and the probe would
 * get a 401.
 *
 * **Why that class of divergence is dangerous.** A 401 at `initialize` is a
 * REAL observation of failure, so the probe records `down` — not the
 * non-escalating `transient_error` that ENG-8345's key-derivation bug produced.
 * `down` crosses `DEFAULT_HARD_DOWN_THRESHOLD` within ~3 cycles, opening an
 * `integration_down` alert and driving the status-reconcile / needs-reauth path
 * on a working integration. A false hard-down is strictly worse than a false
 * alarm.
 *
 * So the env-var NAMES, the extra-header parser and the header assembly all
 * live here, in the one package both sides already depend on, and both sides
 * import them. Neither can grow an input the other doesn't read.
 *
 * Deliberately dependency-free (no imports): the proxy is bundled standalone to
 * `~/.augmented/_mcp/remote-oauth-proxy.js` and is exported on its own core
 * subpath so importing it never drags the integrations barrel into that bundle.
 */

/**
 * The `AGT_REMOTE_MCP_*` env keys a stdio-proxy `.mcp.json` entry carries.
 * Canonical — a reader that hardcodes one of these strings instead of reading
 * it from here is how the probe/proxy pair drifted in the first place.
 *
 * `url` / `tokenFile` / `tokenVar` / `label` are the ENG-6859 originals;
 * `authHeader` / `extraHeaders` are ENG-7748; `toolAllowlist` / `preEnable`
 * are ENG-6948 / ENG-7629 and are proxy-behaviour knobs rather than auth
 * inputs (listed so the table stays the complete contract).
 */
export const REMOTE_MCP_PROXY_ENV = {
  url: 'AGT_REMOTE_MCP_URL',
  tokenFile: 'AGT_REMOTE_MCP_TOKEN_FILE',
  tokenVar: 'AGT_REMOTE_MCP_TOKEN_VAR',
  label: 'AGT_REMOTE_MCP_LABEL',
  authHeader: 'AGT_REMOTE_MCP_AUTH_HEADER',
  extraHeaders: 'AGT_REMOTE_MCP_EXTRA_HEADERS',
  toolAllowlist: 'AGT_REMOTE_MCP_TOOL_ALLOWLIST',
  preEnableToolsets: 'AGT_REMOTE_MCP_PREENABLE_TOOLSETS',
  // ENG-8512: per-provider argument rules. A `tools/call` carrying a value we
  // KNOW the remote accepts and silently ignores is refused before it is
  // forwarded, rather than returning a 200 that every layer above records as
  // success. Deliberately NOT in REMOTE_MCP_AUTH_ENV_KEYS: it shapes which calls
  // we forward, not what an outbound request looks like, so the probe (which
  // never issues a tools/call) has nothing to reconstruct from it.
  argRejects: 'AGT_REMOTE_MCP_ARG_REJECTS',
  // ENG-10614: the per-tool HITL floor table, as JSON `{ "<tool>": "<tier>" }`.
  //
  // WHY THIS EXISTS AT ALL. A hosted API-key remote MCP is called by Claude Code
  // straight at the vendor, so none of the three brokered HITL gates
  // (`enforceComposioHitlGate`, `hitlGateForBrokerCall`, the CLI lane) is in the
  // path, and `tool_definitions.min_hitl_tier` was read by nothing on this lane
  // — measured: zero hits for that column under `packages/core/src/provisioning`,
  // `packages/mcp/src` or `apps/cli/src`. The catalog said `delete_item` needed
  // approval and the call went straight out.
  //
  // Deliberately NOT in REMOTE_MCP_AUTH_ENV_KEYS, for the same reason as
  // `argRejects`: it shapes WHICH calls we forward, not what an outbound request
  // looks like, so the connectivity probe has nothing to reconstruct from it.
  toolTiers: 'AGT_REMOTE_MCP_TOOL_TIERS',
} as const;

/**
 * The subset of {@link REMOTE_MCP_PROXY_ENV} that changes what an outbound
 * request looks like. Every one of these must be honoured by BOTH the proxy and
 * the probe's reconstruction — that is exactly the parity ENG-8357 restores,
 * and the drift-guard test asserts against this list rather than a copy.
 */
export const REMOTE_MCP_AUTH_ENV_KEYS = [
  REMOTE_MCP_PROXY_ENV.tokenVar,
  REMOTE_MCP_PROXY_ENV.authHeader,
  REMOTE_MCP_PROXY_ENV.extraHeaders,
] as const;

/** One `HeaderName:VAR_NAME` pair from {@link REMOTE_MCP_PROXY_ENV.extraHeaders}. */
export interface RemoteMcpExtraHeader {
  header: string;
  varName: string;
}

/** Auth-shaping config resolved from a proxy entry's `env` block. */
export interface RemoteMcpAuthConfig {
  /**
   * Header name that carries the credential. Empty ⇒ the default
   * `Authorization: Bearer <token>`.
   */
  authHeader: string;
  extras: RemoteMcpExtraHeader[];
}

/**
 * ENG-7748: parse the `AGT_REMOTE_MCP_EXTRA_HEADERS` value — comma-separated
 * `HeaderName:VAR_NAME` pairs — into an ordered list. Malformed / empty entries
 * are skipped. The var is looked up per request by the caller's `readVar` (see
 * {@link buildForwardHeaders}), which is where the proxy and the probe
 * legitimately differ: the proxy reads the token file live, the probe reads the
 * agent's overlaid `.env.integrations`.
 */
export function parseExtraHeaders(raw: string): RemoteMcpExtraHeader[] {
  const out: RemoteMcpExtraHeader[] = [];
  for (const part of (raw || '').split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const colon = trimmed.indexOf(':');
    if (colon <= 0) continue; // need a non-empty header name before the ':'
    const header = trimmed.slice(0, colon).trim();
    const varName = trimmed.slice(colon + 1).trim();
    if (header && varName) out.push({ header, varName });
  }
  return out;
}

/**
 * Read the auth-shaping config out of a proxy entry's `env` block (or
 * `process.env`, which is the same map once Claude Code has spawned the proxy).
 *
 * This is the function that NAMES which vars matter. Callers that go through it
 * cannot silently miss a var the other side honours — which is precisely how the
 * probe ended up Bearer-only.
 */
export function readRemoteMcpAuthConfig(
  env: Record<string, string | undefined> | undefined,
): RemoteMcpAuthConfig {
  return {
    authHeader: (env?.[REMOTE_MCP_PROXY_ENV.authHeader] ?? '').trim(),
    extras: parseExtraHeaders(env?.[REMOTE_MCP_PROXY_ENV.extraHeaders] ?? ''),
  };
}

/**
 * ENG-7748: assemble the outbound headers for a request to the remote MCP.
 * Pure (config passed in) so it is unit-testable without env gymnastics.
 *   - JSON-RPC content-negotiation headers,
 *   - the TOKEN_VAR credential, sent either as `authHeader` (e.g. anchor-api-key)
 *     when set, or the default `Authorization: Bearer <token>`,
 *   - each EXTRA_HEADERS pair, its value read via `readVar`. **Empty is
 *     allowed on purpose**: a not-yet-minted session id ships as an empty
 *     header exactly as the direct-HTTP entry did — the target treats it as no
 *     session. A caller must NOT upgrade an unreadable extra var into a "skip
 *     the request" signal; `ANCHOR_BROWSER_SESSION_ID` is in the CLI's
 *     `LATE_BOUND_VARS` and is designed to be empty at probe time, so doing so
 *     would trade the false `down` for a perpetual false `transient_error`
 *     (the ENG-6428 / ENG-8205 shape).
 *
 * Both `authHeader` empty and `extras` empty ⇒ byte-identical to the original
 * Bearer-only behaviour, for every integration that carries neither.
 */
export function buildForwardHeaders(
  token: string,
  authHeader: string,
  extras: readonly RemoteMcpExtraHeader[],
  readVar: (varName: string) => string | null,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/event-stream',
  };
  if (authHeader) headers[authHeader] = token;
  else headers['Authorization'] = `Bearer ${token}`;
  for (const { header, varName } of extras) {
    headers[header] = readVar(varName) ?? '';
  }
  return headers;
}
